// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// The first route is `/maintenance`, and it is the DUE list rather than the
// list of plans — because the question this module exists to answer is «¿qué
// toca?», and a screen that opens on an alphabetical list of everything the
// company maintains answers a question nobody asked.

export const routes = [
  {
    path: '/maintenance',
    name: 'maintenance',
    component: () => import('./pages/MaintenancePage.vue'),
    meta: { permission: 'maintenance:read', title: 'maintenance.title' },
  },
  {
    path: '/maintenance/plans',
    name: 'maintenance-plans',
    component: () => import('./pages/PlansPage.vue'),
    meta: { permission: 'maintenance:read', title: 'maintenance.plans.title' },
  },
  {
    path: '/maintenance/plans/new',
    name: 'maintenance-plan-new',
    component: () => import('./pages/PlanFormPage.vue'),
    meta: { permission: 'maintenance:write', title: 'maintenance.form.newTitle' },
  },
  // Antes de `/:id`, o «calendar» y «templates» se leerían como el id de un plan.
  {
    path: '/maintenance/calendar',
    name: 'maintenance-calendar',
    component: () => import('./pages/CalendarPage.vue'),
    meta: { permission: 'maintenance:read', title: 'maintenance.calendar.title' },
  },
  {
    path: '/maintenance/templates',
    name: 'maintenance-templates',
    component: () => import('./pages/TemplatesPage.vue'),
    meta: { permission: 'maintenance:read', title: 'maintenance.templates.title' },
  },
  {
    path: '/maintenance/:id',
    name: 'maintenance-plan',
    component: () => import('./pages/PlanPage.vue'),
    meta: { permission: 'maintenance:read', title: 'maintenance.plan.title' },
  },
  {
    path: '/maintenance/:id/edit',
    name: 'maintenance-plan-edit',
    component: () => import('./pages/PlanFormPage.vue'),
    meta: { permission: 'maintenance:write', title: 'maintenance.form.editTitle' },
  },
]

// Two entries, and the second one is a child: what is due is the daily screen,
// and the catalogue of plans is where somebody goes once a month to change one.
export const navigation = [
  {
    to: '/maintenance',
    labelKey: 'maintenance.title',
    permission: 'maintenance:read',
    icon: 'gauge',
    group: 'business',
    children: [
      { to: '/maintenance', labelKey: 'maintenance.due.title', permission: 'maintenance:read' },
      { to: '/maintenance/calendar', labelKey: 'maintenance.calendar.title', permission: 'maintenance:read' },
      { to: '/maintenance/plans', labelKey: 'maintenance.plans.title', permission: 'maintenance:read' },
      { to: '/maintenance/templates', labelKey: 'maintenance.templates.title', permission: 'maintenance:read' },
    ],
  },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (section 16): a module that ships two is a module that cannot be
// sold to the third client.
const baseMessages = {
  es: {
    maintenance: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        overdue: 'un mantenimiento vencido | {count} mantenimientos vencidos',
        soon: '{count} mantenimientos que tocan pronto',
        soonOne: '{title} de {subject} el {day}',
        soonOneAlone: '{title} el {day}',
      },
      // Las tarjetas del asistente (backend/assistant.js).
      assistant: {
        complete: {
          action: 'Dar por hecho un mantenimiento',
          field: { date: 'Hecho el', meter: 'Lectura del contador', notes: 'Notas' },
          where: 'Plan de mantenimiento',
          needsMeter: 'Este plan va por contador: di la lectura',
          outcome: '{code} hecho; el siguiente, {next}',
        },
      },
      // El único aviso de este módulo, y va a una persona: el trabajo que
      // vuelve solo se hace si alguien se acuerda.
      notifications: {
        categories: { due: 'Un mantenimiento que toca' },
        due: {
          title: 'Te toca',
          body: '{name}{target}',
        },
      },
      title: 'Mantenimiento',
      dashboard: {
        due: 'Mantenimientos vencidos',
        dueHelp: 'Cuántos planes activos han pasado de su fecha sin que nadie los haya dado por hechos.',
        soon: 'Vencen pronto',
        soonHelp: 'Cuántos planes vencen dentro de los días de aviso configurados en los ajustes.',
        completedMonth: 'Hechos este mes',
        completedMonthHelp: 'Cuántos mantenimientos se han dado por hechos desde el día 1 del mes en curso.',
        duePlans: 'Lo que toca',
        duePlansHelp: 'Los planes más urgentes, del más retrasado al que vence antes. Estira la tarjeta y caben más.',
      },
      subtitle: 'Lo que toca, lo que va vencido y lo que ya se hizo',
      actions: {
        new: 'Nuevo plan', edit: 'Editar', complete: 'Marcar como hecho',
        pause: 'Pausar', resume: 'Reactivar', archive: 'Archivar',
        createOrder: 'Crear orden',
        save: 'Guardar', cancelAction: 'Cancelar', confirm: 'Confirmar', back: 'Volver',
      },
      filters: {
        search: 'Buscar', searchPlaceholder: 'Código, plan, sobre qué…',
        kind: 'Tipo', allKinds: 'Todos',
        status: 'Estado', allStatuses: 'Todos',
      },
      status: { active: 'Activo', paused: 'Pausado', archived: 'Archivado' },

      due: {
        title: 'Preventivo',
        subtitle: 'Vencido primero, y después lo que llega dentro de {days} días',
        none: 'No hay nada pendiente',
        noneHint: 'Todo lo que tenéis en mantenimiento está al día.',
        overdueBy: 'Vencido hace {days} días',
        dueToday: 'Toca hoy',
        dueIn: 'Dentro de {days} días',
        byMeter: 'Pasado de {units} {unit}',
        window: 'Próximos {days} días',
      },
      plans: {
        title: 'Planes',
        subtitle: 'Qué se mantiene, cada cuánto y qué hay que hacer',
      },
      calendar: {
        title: 'Paradas',
        subtitle: 'Cuándo para cada línea, y qué se aprovecha para hacer dentro',
        today: 'Hoy',
        allSites: 'Todos los centros',
        newTitle: 'Nueva parada',
        name: 'Cómo se llama',
        namePlaceholder: 'Parada de agosto de la línea 3…',
        from: 'Desde',
        to: 'Hasta',
        site: 'Centro',
        siteHint: 'Vacío = toda la empresa',
        notes: 'Notas',
        inside: '{count} planes dentro de la ventana',
        addPlan: 'Meter un plan en la parada',
        addPlanHint: 'El plan sigue con su reloj: meterlo aquí no mueve su fecha',
        saved: 'Parada guardada',
        dueThatDay: '{count} vencen ese día',
        dueLegend: 'Mantenimientos que vencen',
        status: {
          planned: 'Planificada', running: 'Parada ahora', done: 'Hecha', cancelled: 'Anulada',
        },
        actions: { new: 'Nueva parada' },
      },
      templates: {
        title: 'Plantillas',
        subtitle: 'Un plan escrito una vez y estampado sobre las máquinas que hagan falta',
        template: 'Plantilla',
        newTitle: 'Nueva plantilla',
        editTitle: 'Editar la plantilla',
        titlePlaceholder: 'Engrase trimestral de bomba…',
        checklistHint: 'Se copian en cada plan: marcar un paso es marcarlo en una máquina, no en las ochenta',
        usage: '{total} planes salieron de aquí · {following} siguen la plantilla',
        detached: '{count} se soltaron al editarlos',
        applyTo: 'Aplicarla a',
        applyToHint: 'Ve añadiendo máquinas. Una que ya tenga plan de esta plantilla se salta sola',
        pickPlaceholder: 'Busca una máquina…',
        everyCalendar: 'cada {every} {unit}',
        everyMeter: 'cada {every} {unit}',
        saved: 'Plantilla guardada',
        applied: '{created} planes creados · {skipped} ya lo tenían',
        pushed: '{count} planes actualizados',
        archived: 'Plantilla archivada',
        restored: 'Plantilla devuelta al catálogo',
        showArchived: 'Ver archivadas',
        actions: {
          new: 'Nueva plantilla',
          apply: 'Crear {count} planes',
          push: 'Bajar los cambios a {count}',
          pushConfirm: 'Bajarlos',
          archive: 'Archivar',
        },
        pushDialog: {
          title: 'Bajar los cambios',
          message: 'Se reescriben el título, el intervalo, las instrucciones y los pasos de {count} planes. No se toca lo que ya se hizo ni cuándo se hizo. Los que alguien editó a mano no se tocan.',
        },
        archiveDialog: {
          title: 'Archivar la plantilla',
          message: 'Deja de poder aplicarse y de bajar cambios. Los planes que salieron de ella siguen tal cual, funcionando.',
        },
        empty: {
          title: 'Ninguna plantilla todavía',
          description: 'Sirven cuando el mismo mantenimiento se repite en muchas máquinas: se escribe una vez y se estampa sobre las ochenta bombas.',
        },
      },
      table: {
        code: 'Código', plan: 'Plan', subject: 'Sobre qué', kind: 'Tipo',
        nextDue: 'Próximo', lastDone: 'Último', status: 'Estado', every: 'Cada',
      },
      kpi: { due: 'Vencido', soon: 'Esta semana', completed: 'Hechos', plans: 'Planes activos' },
      empty: {
        none: 'Todavía no hay planes',
        noneHint: 'Da de alta el primero: una máquina, una furgoneta, un ascensor — y cada cuánto le toca.',
        filtered: 'Sin resultados',
        filteredHint: 'Prueba a quitar algún filtro o a buscar otra cosa.',
      },
      pager: { previous: 'Anterior', next: 'Siguiente', of: 'Página {page} de {pages}' },

      form: {
        newTitle: 'Nuevo plan', editTitle: 'Editar plan',
        title: 'Qué es', titlePlaceholder: 'Revisión semestral, cambio de aceite…',
        kind: 'Tipo de mantenimiento',
        subject: 'Sobre qué', subjectHelp: 'Una furgoneta, una ubicación, un cliente — o escríbelo si no está en ningún sitio.',
        subjectType: 'Tipo de cosa',
        subjectTypes: { vehicle: 'Vehículo', location: 'Ubicación del almacén', customer: 'Cliente', project: 'Proyecto' },
        schedule: 'Cada cuánto',
        mode: 'Se cuenta por', every: 'Cada', unit: 'Unidad',
        anchor: 'Y se cuenta desde',
        everyUnits: 'Cada', meterUnit: 'Unidad del contador',
        meterHelp: 'km para furgonetas, h para máquinas.',
        startsOn: 'Empieza el', startsAtMeter: 'Contador al empezar',
        until: 'Termina el', untilHelp: 'Déjalo vacío si no termina, que es lo normal.',
        maxOccurrences: 'Número de veces',
        instructions: 'Qué hay que hacer',
        checklist: 'Comprobaciones', checklistAdd: 'Añadir comprobación',
        checklistHelp: 'Se copian tal cual en cada parte, así que cambiarlas no reescribe lo ya firmado.',
        assignee: 'Responsable', team: 'Equipo', notes: 'Notas internas',
        nextWillBe: 'Con esto, el próximo sería el {date}',
      },
      modes: { calendar: 'Calendario', usage: 'Uso (contador)', both: 'Lo que llegue antes' },
      units: { day: 'Días', week: 'Semanas', month: 'Meses', year: 'Años' },
      anchors: { completion: 'La última vez que se hizo', schedule: 'La fecha que tocaba' },
      // La distinción que casi ningún programa hace, explicada donde se elige.
      anchorHelp: {
        completion: 'El reloj arranca cuando se hace el trabajo. Un cambio de aceite: si se hace tarde, el siguiente se mueve con él.',
        schedule: 'La fecha es la que era, se llegue a tiempo o no. Una inspección legal: llegar tarde no compra tiempo al otro lado.',
      },

      plan: {
        title: 'Plan de mantenimiento', schedule: 'Cada cuánto',
        lastDone: 'Última vez', nextDue: 'Próxima vez', never: 'Todavía no se ha hecho',
        meter: 'Contador', doneCount: 'Veces hecho',
        history: 'Historial', historyNone: 'Todavía no hay ningún parte',
        pausedNotice: 'Este plan está pausado: no aparece en lo que toca hasta que se reactive.',
        archivedNotice: 'Este plan está archivado. Su historial se conserva entero.',
        finishedNotice: 'Este plan ya ha terminado: llegó a su fecha final o a su número de veces.',
      },
      record: {
        completedAt: 'Fecha', completedBy: 'Quién', meter: 'Contador',
        notes: 'Notas', void: 'Anular este parte', voided: 'Anulado',
      },

      complete: {
        title: 'Marcar como hecho',
        completedAt: 'Cuándo se hizo', meter: 'Lectura del contador',
        meterRequired: 'Este plan va por contador, así que la lectura es de donde sale la próxima.',
        notes: 'Notas', checklist: 'Comprobaciones',
        confirm: 'Guardar el parte', done: 'Parte guardado',
        nextWillBe: 'El siguiente pasará a ser el {date}',
      },
      voidDialog: {
        title: 'Anular el parte',
        question: 'El parte se queda, marcado como anulado, y el plan vuelve a donde diga el historial que queda.',
        reason: 'Motivo', reasonHint: 'Lo leerá alguien que no estaba, dentro de tres años.',
      },

      errors: {
        ARCHIVED: 'Este plan está archivado',
        BAD_DATE: 'Eso no es una fecha',
        IN_THE_FUTURE: 'Un parte no puede llevar fecha futura',
        BAD_METER: 'La lectura tiene que ser un número, y no negativo',
        METER_WENT_BACK: 'Esa lectura es menor que la última anotada en este plan',
        METER_IMPLAUSIBLE: 'Esa lectura está demasiado lejos de la última para ser una lectura; la puede anotar quien gestiona el mantenimiento',
        JUST_COMPLETED: 'Este plan se acaba de cerrar hace un momento',
        METER_REQUIRED: 'Este plan va por contador: sin la lectura no se puede calcular el siguiente',
      },
      search: { subtitle: 'Plan de mantenimiento' },
    },

    // ── Cómo se llama este módulo en las pantallas de la plataforma ──────────
    modules: { maintenance: 'Mantenimiento' },
    assistant: {
      openers: {
        maintenance: {
          'what-is-due': '¿Qué mantenimiento toca esta semana?',
          'what-is-overdue': '¿Qué mantenimiento está vencido?',
          'last-serviced': '¿Cuándo se revisó esto por última vez?',
        },
      },
    },
    permissions: {
      maintenance: {
        read: 'Ver los planes y lo que toca',
        complete: 'Marcar un mantenimiento como hecho',
        write: 'Crear y editar planes de mantenimiento',
        manage: 'Pausar, archivar y anular partes',
      },
    },
    settings: {
      ns: {
        maintenance: {
          title: 'Mantenimiento',
          subtitle: 'Cómo se llaman aquí los mantenimientos y con cuánta antelación se avisa',
          planKinds: {
            label: 'Tipos de mantenimiento que hace esta empresa',
            help: 'Son los que ofrece el selector de cada plan.',
          },
          noticeDays: { label: 'Días de antelación con los que algo empieza a aparecer como que llega' },
          defaultMeterUnit: {
            label: 'Unidad del contador por defecto',
            help: 'km para furgonetas, h para máquinas. Se puede cambiar en cada plan.',
          },
          announceDue: {
            label: 'Avisar por el bus cuando un plan vence',
            help: 'Permite que otros módulos reaccionen — por ejemplo, que se abra una orden de trabajo sola. Aquí no se crea nada.',
          },
        },
      },
    },
    audit: {
      actions: {
        maintenance: {
          plan_created: 'Plan de mantenimiento creado',
          template_created: 'Plantilla de mantenimiento creada',
          template_updated: 'Plantilla modificada',
          template_archived: 'Plantilla archivada',
          template_restored: 'Plantilla devuelta al catálogo',
          template_applied: 'Plantilla aplicada a varias máquinas',
          template_pushed: 'Cambios de la plantilla bajados a sus planes',
          shutdown_planned: 'Parada de planta planificada',
          shutdown_updated: 'Parada de planta modificada',
          shutdown_status_changed: 'Estado de la parada cambiado',
          plan_updated: 'Plan de mantenimiento modificado',
          plan_paused: 'Plan pausado',
          plan_resumed: 'Plan reactivado',
          plan_archived: 'Plan archivado',
          completed: 'Mantenimiento marcado como hecho',
          record_voided: 'Parte de mantenimiento anulado',
        },
      },
    },
  },

  ca: {
    maintenance: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        overdue: 'un manteniment vençut | {count} manteniments vençuts',
        soon: '{count} manteniments que toquen aviat',
        soonOne: '{title} de {subject} el {day}',
        soonOneAlone: '{title} el {day}',
      },
      assistant: {
        complete: {
          action: 'Donar per fet un manteniment',
          field: { date: 'Fet el', meter: 'Lectura del comptador', notes: 'Notes' },
          where: 'Pla de manteniment',
          needsMeter: 'Aquest pla va per comptador: digues la lectura',
          outcome: '{code} fet; el següent, {next}',
        },
      },
      notifications: {
        categories: { due: 'Un manteniment que toca' },
        due: {
          title: 'Et toca',
          body: '{name}{target}',
        },
      },
      title: 'Manteniment',
      dashboard: {
        due: 'Manteniments vençuts',
        dueHelp: 'Quants plans actius han passat de la seva data sense que ningú els hagi donat per fets.',
        soon: 'Vencen aviat',
        soonHelp: 'Quants plans vencen dins dels dies d\'avís configurats als ajustos.',
        completedMonth: 'Fets aquest mes',
        completedMonthHelp: 'Quants manteniments s\'han donat per fets des del dia 1 del mes en curs.',
        duePlans: 'El que toca',
        duePlansHelp: 'Els plans més urgents, del més endarrerit al que venç abans. Estira la targeta i n\'hi caben més.',
      },
      subtitle: 'El que toca, el que ha vençut i el que ja s’ha fet',
      actions: {
        new: 'Nou pla', edit: 'Editar', complete: 'Marcar com a fet',
        pause: 'Pausar', resume: 'Reactivar', archive: 'Arxivar',
        createOrder: 'Crear ordre',
        save: 'Desar', cancelAction: 'Cancel·lar', confirm: 'Confirmar', back: 'Tornar',
      },
      filters: {
        search: 'Cercar', searchPlaceholder: 'Codi, pla, sobre què…',
        kind: 'Tipus', allKinds: 'Tots',
        status: 'Estat', allStatuses: 'Tots',
      },
      status: { active: 'Actiu', paused: 'Pausat', archived: 'Arxivat' },

      due: {
        title: 'Preventiu',
        subtitle: 'Primer el vençut, i després el que arriba d’aquí a {days} dies',
        none: 'No hi ha res pendent',
        noneHint: 'Tot el que teniu en manteniment està al dia.',
        overdueBy: 'Vençut fa {days} dies',
        dueToday: 'Toca avui',
        dueIn: 'D’aquí a {days} dies',
        byMeter: 'Passat de {units} {unit}',
        window: 'Pròxims {days} dies',
      },
      plans: {
        title: 'Plans',
        subtitle: 'Què es manté, cada quant i què cal fer',
      },
      calendar: {
        title: 'Parades',
        subtitle: 'Quan para cada línia, i què s’aprofita per fer a dins',
        today: 'Avui',
        allSites: 'Tots els centres',
        newTitle: 'Nova parada',
        name: 'Com es diu',
        namePlaceholder: 'Parada d’agost de la línia 3…',
        from: 'Des de',
        to: 'Fins a',
        site: 'Centre',
        siteHint: 'Buit = tota l’empresa',
        notes: 'Notes',
        inside: '{count} plans dins de la finestra',
        addPlan: 'Ficar un pla a la parada',
        addPlanHint: 'El pla segueix amb el seu rellotge: ficar-lo aquí no mou la seva data',
        saved: 'Parada desada',
        dueThatDay: '{count} vencen aquell dia',
        dueLegend: 'Manteniments que vencen',
        status: {
          planned: 'Planificada', running: 'Parada ara', done: 'Feta', cancelled: 'Anul·lada',
        },
        actions: { new: 'Nova parada' },
      },
      templates: {
        title: 'Plantilles',
        subtitle: 'Un pla escrit una vegada i estampat sobre les màquines que calgui',
        template: 'Plantilla',
        newTitle: 'Nova plantilla',
        editTitle: 'Editar la plantilla',
        titlePlaceholder: 'Greixatge trimestral de bomba…',
        checklistHint: 'Es copien a cada pla: marcar un pas és marcar-lo en una màquina, no en les vuitanta',
        usage: '{total} plans van sortir d’aquí · {following} segueixen la plantilla',
        detached: '{count} es van deslligar en editar-los',
        applyTo: 'Aplicar-la a',
        applyToHint: 'Ves afegint màquines. Una que ja tingui pla d’aquesta plantilla se salta sola',
        pickPlaceholder: 'Cerca una màquina…',
        everyCalendar: 'cada {every} {unit}',
        everyMeter: 'cada {every} {unit}',
        saved: 'Plantilla desada',
        applied: '{created} plans creats · {skipped} ja el tenien',
        pushed: '{count} plans actualitzats',
        archived: 'Plantilla arxivada',
        restored: 'Plantilla retornada al catàleg',
        showArchived: 'Veure arxivades',
        actions: {
          new: 'Nova plantilla',
          apply: 'Crear {count} plans',
          push: 'Baixar els canvis a {count}',
          pushConfirm: 'Baixar-los',
          archive: 'Arxivar',
        },
        pushDialog: {
          title: 'Baixar els canvis',
          message: 'Es reescriuen el títol, l’interval, les instruccions i els passos de {count} plans. No es toca el que ja es va fer ni quan. Els que algú va editar a mà no es toquen.',
        },
        archiveDialog: {
          title: 'Arxivar la plantilla',
          message: 'Deixa de poder aplicar-se i de baixar canvis. Els plans que en van sortir segueixen tal qual, funcionant.',
        },
        empty: {
          title: 'Cap plantilla encara',
          description: 'Serveixen quan el mateix manteniment es repeteix en moltes màquines: s’escriu una vegada i s’estampa sobre les vuitanta bombes.',
        },
      },
      table: {
        code: 'Codi', plan: 'Pla', subject: 'Sobre què', kind: 'Tipus',
        nextDue: 'Pròxim', lastDone: 'Últim', status: 'Estat', every: 'Cada',
      },
      kpi: { due: 'Vençut', soon: 'Aquesta setmana', completed: 'Fets', plans: 'Plans actius' },
      empty: {
        none: 'Encara no hi ha plans',
        noneHint: 'Dona d’alta el primer: una màquina, una furgoneta, un ascensor — i cada quant li toca.',
        filtered: 'Sense resultats',
        filteredHint: 'Prova de treure algun filtre o de cercar una altra cosa.',
      },
      pager: { previous: 'Anterior', next: 'Següent', of: 'Pàgina {page} de {pages}' },

      form: {
        newTitle: 'Nou pla', editTitle: 'Editar pla',
        title: 'Què és', titlePlaceholder: 'Revisió semestral, canvi d’oli…',
        kind: 'Tipus de manteniment',
        subject: 'Sobre què', subjectHelp: 'Una furgoneta, una ubicació, un client — o escriu-ho si no és enlloc.',
        subjectType: 'Tipus de cosa',
        subjectTypes: { vehicle: 'Vehicle', location: 'Ubicació del magatzem', customer: 'Client', project: 'Projecte' },
        schedule: 'Cada quant',
        mode: 'Es compta per', every: 'Cada', unit: 'Unitat',
        anchor: 'I es compta des de',
        everyUnits: 'Cada', meterUnit: 'Unitat del comptador',
        meterHelp: 'km per a furgonetes, h per a màquines.',
        startsOn: 'Comença el', startsAtMeter: 'Comptador en començar',
        until: 'Acaba el', untilHelp: 'Deixa-ho buit si no acaba, que és el normal.',
        maxOccurrences: 'Nombre de vegades',
        instructions: 'Què cal fer',
        checklist: 'Comprovacions', checklistAdd: 'Afegir comprovació',
        checklistHelp: 'Es copien tal qual a cada part, així que canviar-les no reescriu el ja signat.',
        assignee: 'Responsable', team: 'Equip', notes: 'Notes internes',
        nextWillBe: 'Amb això, el pròxim seria el {date}',
      },
      modes: { calendar: 'Calendari', usage: 'Ús (comptador)', both: 'El que arribi abans' },
      units: { day: 'Dies', week: 'Setmanes', month: 'Mesos', year: 'Anys' },
      anchors: { completion: 'L’última vegada que es va fer', schedule: 'La data que tocava' },
      anchorHelp: {
        completion: 'El rellotge arrenca quan es fa la feina. Un canvi d’oli: si es fa tard, el següent es mou amb ell.',
        schedule: 'La data és la que era, s’hi arribi a temps o no. Una inspecció legal: arribar tard no compra temps a l’altre costat.',
      },

      plan: {
        title: 'Pla de manteniment', schedule: 'Cada quant',
        lastDone: 'Última vegada', nextDue: 'Pròxima vegada', never: 'Encara no s’ha fet',
        meter: 'Comptador', doneCount: 'Vegades fet',
        history: 'Historial', historyNone: 'Encara no hi ha cap part',
        pausedNotice: 'Aquest pla està pausat: no apareix al que toca fins que es reactivi.',
        archivedNotice: 'Aquest pla està arxivat. El seu historial es conserva sencer.',
        finishedNotice: 'Aquest pla ja ha acabat: ha arribat a la data final o al nombre de vegades.',
      },
      record: {
        completedAt: 'Data', completedBy: 'Qui', meter: 'Comptador',
        notes: 'Notes', void: 'Anul·lar aquest part', voided: 'Anul·lat',
      },

      complete: {
        title: 'Marcar com a fet',
        completedAt: 'Quan s’ha fet', meter: 'Lectura del comptador',
        meterRequired: 'Aquest pla va per comptador, així que la lectura és d’on surt la pròxima.',
        notes: 'Notes', checklist: 'Comprovacions',
        confirm: 'Desar el part', done: 'Part desat',
        nextWillBe: 'El següent passarà a ser el {date}',
      },
      voidDialog: {
        title: 'Anul·lar el part',
        question: 'El part es queda, marcat com anul·lat, i el pla torna on digui l’historial que queda.',
        reason: 'Motiu', reasonHint: 'Ho llegirà algú que no hi era, d’aquí a tres anys.',
      },

      errors: {
        ARCHIVED: 'Aquest pla està arxivat',
        BAD_DATE: 'Això no és una data',
        IN_THE_FUTURE: 'Un part no pot portar data futura',
        BAD_METER: 'La lectura ha de ser un número, i no negatiu',
        METER_WENT_BACK: 'Aquesta lectura és menor que l’última anotada en aquest pla',
        METER_IMPLAUSIBLE: 'Aquesta lectura és massa lluny de l’última per ser una lectura; la pot anotar qui gestiona el manteniment',
        JUST_COMPLETED: 'Aquest pla s’acaba de tancar fa un moment',
        METER_REQUIRED: 'Aquest pla va per comptador: sense la lectura no es pot calcular el següent',
      },
      search: { subtitle: 'Pla de manteniment' },
    },

    modules: { maintenance: 'Manteniment' },
    assistant: {
      openers: {
        maintenance: {
          'what-is-due': 'Quin manteniment toca aquesta setmana?',
          'what-is-overdue': 'Quin manteniment està vençut?',
          'last-serviced': 'Quan es va revisar això per última vegada?',
        },
      },
    },
    permissions: {
      maintenance: {
        read: 'Veure els plans i el que toca',
        complete: 'Marcar un manteniment com a fet',
        write: 'Crear i editar plans de manteniment',
        manage: 'Pausar, arxivar i anul·lar parts',
      },
    },
    settings: {
      ns: {
        maintenance: {
          title: 'Manteniment',
          subtitle: 'Com es diuen aquí els manteniments i amb quanta antelació s’avisa',
          planKinds: {
            label: 'Tipus de manteniment que fa aquesta empresa',
            help: 'Són els que ofereix el selector de cada pla.',
          },
          noticeDays: { label: 'Dies d’antelació amb què una cosa comença a aparèixer com que arriba' },
          defaultMeterUnit: {
            label: 'Unitat del comptador per defecte',
            help: 'km per a furgonetes, h per a màquines. Es pot canviar a cada pla.',
          },
          announceDue: {
            label: 'Avisar pel bus quan un pla venç',
            help: 'Permet que altres mòduls reaccionin — per exemple, que s’obri una ordre de treball sola. Aquí no es crea res.',
          },
        },
      },
    },
    audit: {
      actions: {
        maintenance: {
          plan_created: 'Pla de manteniment creat',
          template_created: 'Plantilla de manteniment creada',
          template_updated: 'Plantilla modificada',
          template_archived: 'Plantilla arxivada',
          template_restored: 'Plantilla retornada al catàleg',
          template_applied: 'Plantilla aplicada a diverses màquines',
          template_pushed: 'Canvis de la plantilla baixats als seus plans',
          shutdown_planned: 'Parada de planta planificada',
          shutdown_updated: 'Parada de planta modificada',
          shutdown_status_changed: 'Estat de la parada canviat',
          plan_updated: 'Pla de manteniment modificat',
          plan_paused: 'Pla pausat',
          plan_resumed: 'Pla reactivat',
          plan_archived: 'Pla arxivat',
          completed: 'Manteniment marcat com a fet',
          record_voided: 'Part de manteniment anul·lat',
        },
      },
    },
  },

  en: {
    maintenance: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        overdue: 'one overdue maintenance plan | {count} overdue maintenance plans',
        soon: '{count} maintenance plans coming up',
        soonOne: '{title} on {subject} on {day}',
        soonOneAlone: '{title} on {day}',
      },
      assistant: {
        complete: {
          action: 'File maintenance as done',
          field: { date: 'Done on', meter: 'Meter reading', notes: 'Notes' },
          where: 'Maintenance plan',
          needsMeter: 'This plan runs on a meter: say the reading',
          outcome: '{code} done; the next one, {next}',
        },
      },
      notifications: {
        categories: { due: 'Maintenance that has come due' },
        due: {
          title: 'It is your turn',
          body: '{name}{target}',
        },
      },
      title: 'Maintenance',
      dashboard: {
        due: 'Maintenance overdue',
        dueHelp: 'How many live plans are past their date with nobody having ticked them off.',
        soon: 'Falling due soon',
        soonHelp: 'How many plans fall due within the notice days set in the settings.',
        completedMonth: 'Done this month',
        completedMonthHelp: 'How many maintenances have been ticked off since the 1st of the current month.',
        duePlans: 'What is due',
        duePlansHelp: 'The most urgent plans, from the latest to the one falling due soonest. Stretch the card and more fit.',
      },
      subtitle: 'What is due, what is late, and what has already been done',
      actions: {
        new: 'New plan', edit: 'Edit', complete: 'Mark as done',
        pause: 'Pause', resume: 'Resume', archive: 'Archive',
        createOrder: 'Create order',
        save: 'Save', cancelAction: 'Cancel', confirm: 'Confirm', back: 'Back',
      },
      filters: {
        search: 'Search', searchPlaceholder: 'Code, plan, what it is on…',
        kind: 'Kind', allKinds: 'All',
        status: 'State', allStatuses: 'All',
      },
      status: { active: 'Active', paused: 'Paused', archived: 'Archived' },

      due: {
        title: 'Preventive',
        subtitle: 'Late first, then what arrives within {days} days',
        none: 'Nothing is waiting',
        noneHint: 'Everything you maintain is up to date.',
        overdueBy: '{days} days late',
        dueToday: 'Due today',
        dueIn: 'In {days} days',
        byMeter: '{units} {unit} past',
        window: 'Next {days} days',
      },
      plans: {
        title: 'Plans',
        subtitle: 'What is maintained, how often, and what has to be done',
      },
      calendar: {
        title: 'Shutdowns',
        subtitle: 'When each line stops, and what gets done while it is down',
        today: 'Today',
        allSites: 'Every site',
        newTitle: 'New shutdown',
        name: 'What it is called',
        namePlaceholder: 'August shutdown, line 3…',
        from: 'From',
        to: 'To',
        site: 'Site',
        siteHint: 'Empty = the whole company',
        notes: 'Notes',
        inside: '{count} plans inside the window',
        addPlan: 'Put a plan in the shutdown',
        addPlanHint: 'The plan keeps its own clock: putting it here does not move its date',
        saved: 'Shutdown saved',
        dueThatDay: '{count} fall due that day',
        dueLegend: 'Maintenance falling due',
        status: {
          planned: 'Planned', running: 'Down now', done: 'Done', cancelled: 'Cancelled',
        },
        actions: { new: 'New shutdown' },
      },
      templates: {
        title: 'Templates',
        subtitle: 'A plan written once and stamped out over as many machines as it takes',
        template: 'Template',
        newTitle: 'New template',
        editTitle: 'Edit the template',
        titlePlaceholder: 'Quarterly pump greasing…',
        checklistHint: 'Copied onto each plan: ticking a step ticks it off one machine, not eighty',
        usage: '{total} plans came out of this · {following} still follow it',
        detached: '{count} detached when somebody edited them',
        applyTo: 'Apply it to',
        applyToHint: 'Keep adding machines. One that already has a plan from this template is skipped',
        pickPlaceholder: 'Find a machine…',
        everyCalendar: 'every {every} {unit}',
        everyMeter: 'every {every} {unit}',
        saved: 'Template saved',
        applied: '{created} plans created · {skipped} already had one',
        pushed: '{count} plans updated',
        archived: 'Template archived',
        restored: 'Template brought back',
        showArchived: 'Show archived',
        actions: {
          new: 'New template',
          apply: 'Create {count} plans',
          push: 'Push the changes to {count}',
          pushConfirm: 'Push them',
          archive: 'Archive',
        },
        pushDialog: {
          title: 'Push the changes',
          message: 'The title, the interval, the instructions and the steps of {count} plans are rewritten. What was already done and when is not touched. Plans somebody edited by hand are left alone.',
        },
        archiveDialog: {
          title: 'Archive the template',
          message: 'It stops being applicable and stops pushing changes. The plans that came out of it carry on exactly as they are.',
        },
        empty: {
          title: 'No templates yet',
          description: 'They earn their keep when the same maintenance repeats across many machines: written once and stamped out over the eighty pumps.',
        },
      },
      table: {
        code: 'Code', plan: 'Plan', subject: 'On', kind: 'Kind',
        nextDue: 'Next', lastDone: 'Last', status: 'State', every: 'Every',
      },
      kpi: { due: 'Late', soon: 'This week', completed: 'Done', plans: 'Active plans' },
      empty: {
        none: 'No plans yet',
        noneHint: 'Register the first: a machine, a van, a lift — and how often it needs doing.',
        filtered: 'Nothing found',
        filteredHint: 'Try removing a filter or searching for something else.',
      },
      pager: { previous: 'Previous', next: 'Next', of: 'Page {page} of {pages}' },

      form: {
        newTitle: 'New plan', editTitle: 'Edit plan',
        title: 'What it is', titlePlaceholder: 'Six-monthly service, oil change…',
        kind: 'Kind of maintenance',
        subject: 'What it is on', subjectHelp: 'A van, a location, a customer — or type it if it is not on file anywhere.',
        subjectType: 'Kind of thing',
        subjectTypes: { vehicle: 'Vehicle', location: 'Warehouse location', customer: 'Customer', project: 'Project' },
        schedule: 'How often',
        mode: 'Counted by', every: 'Every', unit: 'Unit',
        anchor: 'And counted from',
        everyUnits: 'Every', meterUnit: 'Meter unit',
        meterHelp: 'km for vans, h for machines.',
        startsOn: 'Starts on', startsAtMeter: 'Meter at the start',
        until: 'Ends on', untilHelp: 'Leave it empty if it does not end, which is the normal case.',
        maxOccurrences: 'Number of times',
        instructions: 'What has to be done',
        checklist: 'Checks', checklistAdd: 'Add a check',
        checklistHelp: 'They are copied onto every record as they are, so changing them does not rewrite what was already signed off.',
        assignee: 'Responsible', team: 'Crew', notes: 'Internal notes',
        nextWillBe: 'With this, the next one would be {date}',
      },
      modes: { calendar: 'Calendar', usage: 'Usage (meter)', both: 'Whichever comes first' },
      units: { day: 'Days', week: 'Weeks', month: 'Months', year: 'Years' },
      anchors: { completion: 'The last time it was done', schedule: 'The date it was due' },
      anchorHelp: {
        completion: 'The clock starts when the work is done. An oil change: done late, the next one moves with it.',
        schedule: 'The date is the date, on time or not. A legal inspection: being late buys nothing at the other end.',
      },

      plan: {
        title: 'Maintenance plan', schedule: 'How often',
        lastDone: 'Last done', nextDue: 'Next due', never: 'Never done yet',
        meter: 'Meter', doneCount: 'Times done',
        history: 'History', historyNone: 'No records yet',
        pausedNotice: 'This plan is paused: it stays out of what is due until it is resumed.',
        archivedNotice: 'This plan is archived. Its history is kept in full.',
        finishedNotice: 'This plan has run its course: it reached its end date or its number of times.',
      },
      record: {
        completedAt: 'Date', completedBy: 'Who', meter: 'Meter',
        notes: 'Notes', void: 'Void this record', voided: 'Void',
      },

      complete: {
        title: 'Mark as done',
        completedAt: 'When it was done', meter: 'Meter reading',
        meterRequired: 'This plan runs on a meter, so the reading is what the next one comes from.',
        notes: 'Notes', checklist: 'Checks',
        confirm: 'File the record', done: 'Record filed',
        nextWillBe: 'The next one becomes {date}',
      },
      voidDialog: {
        title: 'Void the record',
        question: 'The record stays, marked void, and the plan goes back to what the surviving history says.',
        reason: 'Reason', reasonHint: 'Somebody who was not there will read this in three years.',
      },

      errors: {
        ARCHIVED: 'This plan is archived',
        BAD_DATE: 'That is not a date',
        IN_THE_FUTURE: 'A record cannot be dated in the future',
        BAD_METER: 'A reading has to be a number, and not a negative one',
        METER_WENT_BACK: 'That reading is lower than the last one filed on this plan',
        METER_IMPLAUSIBLE: 'That reading is too far from the last one to be a reading; somebody who manages maintenance can file it',
        JUST_COMPLETED: 'This plan was completed a moment ago',
        METER_REQUIRED: 'This plan runs on a meter: without the reading the next one cannot be worked out',
      },
      search: { subtitle: 'Maintenance plan' },
    },

    modules: { maintenance: 'Maintenance' },
    assistant: {
      openers: {
        maintenance: {
          'what-is-due': 'What maintenance is due this week?',
          'what-is-overdue': 'What maintenance is overdue?',
          'last-serviced': 'When was this last serviced?',
        },
      },
    },
    permissions: {
      maintenance: {
        read: 'See the plans and what is due',
        complete: 'Mark maintenance as done',
        write: 'Create and edit maintenance plans',
        manage: 'Pause, archive and void records',
      },
    },
    settings: {
      ns: {
        maintenance: {
          title: 'Maintenance',
          subtitle: 'What this company calls its maintenance, and how much notice it gets',
          planKinds: {
            label: 'Kinds of maintenance this company does',
            help: 'They are what the selector offers on each plan.',
          },
          noticeDays: { label: 'Days ahead a plan starts appearing as coming up' },
          defaultMeterUnit: {
            label: 'What a meter is measured in by default',
            help: 'km for vans, h for machines. It can be changed on each plan.',
          },
          announceDue: {
            label: 'Announce on the bus when a plan falls due',
            help: 'Lets other modules react — a work order opening itself, for instance. Nothing is created here.',
          },
        },
      },
    },
    audit: {
      actions: {
        maintenance: {
          plan_created: 'Maintenance plan created',
          template_created: 'Maintenance template created',
          template_updated: 'Template edited',
          template_archived: 'Template archived',
          template_restored: 'Template brought back',
          template_applied: 'Template stamped out over several machines',
          template_pushed: 'Template changes pushed down to its plans',
          shutdown_planned: 'Plant shutdown planned',
          shutdown_updated: 'Plant shutdown edited',
          shutdown_status_changed: 'Shutdown status changed',
          plan_updated: 'Maintenance plan changed',
          plan_paused: 'Plan paused',
          plan_resumed: 'Plan resumed',
          plan_archived: 'Plan archived',
          completed: 'Maintenance marked as done',
          record_voided: 'Maintenance record voided',
        },
      },
    },
  },

  fr: {
    maintenance: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        overdue: 'un entretien en retard | {count} entretiens en retard',
        soon: '{count} entretiens à venir',
        soonOne: '{title} sur {subject} {day}',
        soonOneAlone: '{title} {day}',
      },
      assistant: {
        complete: {
          action: 'Marquer un entretien comme fait',
          field: { date: 'Fait le', meter: 'Relevé du compteur', notes: 'Notes' },
          where: "Plan d'entretien",
          needsMeter: 'Ce plan suit un compteur : indiquez le relevé',
          outcome: '{code} fait ; le prochain, {next}',
        },
      },
      notifications: {
        categories: { due: 'Un entretien arrive à échéance' },
        due: {
          title: "C'est à toi",
          body: '{name}{target}',
        },
      },
      title: 'Maintenance',
      dashboard: {
        due: 'Maintenances échues',
        dueHelp: 'Combien de plans actifs ont dépassé leur date sans que personne ne les ait validés.',
        soon: 'Échéance proche',
        soonHelp: 'Combien de plans arrivent à échéance dans le délai d\'alerte défini dans les réglages.',
        completedMonth: 'Faits ce mois-ci',
        completedMonthHelp: 'Combien de maintenances ont été validées depuis le 1er du mois en cours.',
        duePlans: 'Ce qui est à faire',
        duePlansHelp: 'Les plans les plus urgents, du plus en retard à celui qui arrive le plus tôt. Étirez la carte et il en tient plus.',
      },
      subtitle: 'Ce qui est à faire, ce qui est en retard et ce qui a été fait',
      actions: {
        new: 'Nouveau plan', edit: 'Modifier', complete: 'Marquer comme fait',
        pause: 'Suspendre', resume: 'Reprendre', archive: 'Archiver',
        createOrder: 'Créer un ordre',
        save: 'Enregistrer', cancelAction: 'Annuler', confirm: 'Confirmer', back: 'Retour',
      },
      filters: {
        search: 'Rechercher', searchPlaceholder: 'Code, plan, sur quoi…',
        kind: 'Type', allKinds: 'Tous',
        status: 'État', allStatuses: 'Tous',
      },
      status: { active: 'Actif', paused: 'Suspendu', archived: 'Archivé' },

      due: {
        title: 'Préventif',
        subtitle: 'Le retard d’abord, puis ce qui arrive dans {days} jours',
        none: 'Rien en attente',
        noneHint: 'Tout ce que vous entretenez est à jour.',
        overdueBy: 'En retard de {days} jours',
        dueToday: 'À faire aujourd’hui',
        dueIn: 'Dans {days} jours',
        byMeter: '{units} {unit} de dépassement',
        window: 'Les {days} prochains jours',
      },
      plans: {
        title: 'Plans',
        subtitle: 'Ce qui est entretenu, à quelle fréquence, et ce qu’il faut faire',
      },
      calendar: {
        title: 'Arrêts',
        subtitle: 'Quand chaque ligne s’arrête, et ce qu’on en profite pour faire',
        today: 'Aujourd’hui',
        allSites: 'Tous les sites',
        newTitle: 'Nouvel arrêt',
        name: 'Comment il s’appelle',
        namePlaceholder: 'Arrêt d’août, ligne 3…',
        from: 'Du',
        to: 'Au',
        site: 'Site',
        siteHint: 'Vide = toute l’entreprise',
        notes: 'Notes',
        inside: '{count} plans dans la fenêtre',
        addPlan: 'Mettre un plan dans l’arrêt',
        addPlanHint: 'Le plan garde son horloge : l’y mettre ne déplace pas sa date',
        saved: 'Arrêt enregistré',
        dueThatDay: '{count} arrivent à échéance ce jour-là',
        dueLegend: 'Maintenances à échéance',
        status: {
          planned: 'Planifié', running: 'À l’arrêt', done: 'Fait', cancelled: 'Annulé',
        },
        actions: { new: 'Nouvel arrêt' },
      },
      templates: {
        title: 'Modèles',
        subtitle: 'Un plan écrit une fois et appliqué à autant de machines qu’il le faut',
        template: 'Modèle',
        newTitle: 'Nouveau modèle',
        editTitle: 'Modifier le modèle',
        titlePlaceholder: 'Graissage trimestriel de pompe…',
        checklistHint: 'Copiés sur chaque plan : cocher une étape la coche sur une machine, pas sur les quatre-vingts',
        usage: '{total} plans en sont sortis · {following} le suivent encore',
        detached: '{count} se sont détachés quand on les a modifiés',
        applyTo: 'L’appliquer à',
        applyToHint: 'Ajoutez des machines. Celle qui a déjà un plan de ce modèle est ignorée',
        pickPlaceholder: 'Cherchez une machine…',
        everyCalendar: 'tous les {every} {unit}',
        everyMeter: 'tous les {every} {unit}',
        saved: 'Modèle enregistré',
        applied: '{created} plans créés · {skipped} en avaient déjà un',
        pushed: '{count} plans mis à jour',
        archived: 'Modèle archivé',
        restored: 'Modèle remis au catalogue',
        showArchived: 'Afficher les archivés',
        actions: {
          new: 'Nouveau modèle',
          apply: 'Créer {count} plans',
          push: 'Répercuter sur {count}',
          pushConfirm: 'Répercuter',
          archive: 'Archiver',
        },
        pushDialog: {
          title: 'Répercuter les modifications',
          message: 'Le titre, l’intervalle, les instructions et les étapes de {count} plans sont réécrits. Ce qui a déjà été fait et quand n’est pas touché. Les plans modifiés à la main sont laissés tels quels.',
        },
        archiveDialog: {
          title: 'Archiver le modèle',
          message: 'Il ne peut plus être appliqué ni répercuter de modifications. Les plans qui en sont sortis continuent exactement tels quels.',
        },
        empty: {
          title: 'Aucun modèle pour l’instant',
          description: 'Ils servent quand la même maintenance se répète sur beaucoup de machines : écrite une fois et appliquée aux quatre-vingts pompes.',
        },
      },
      table: {
        code: 'Code', plan: 'Plan', subject: 'Sur', kind: 'Type',
        nextDue: 'Prochain', lastDone: 'Dernier', status: 'État', every: 'Tous les',
      },
      kpi: { due: 'En retard', soon: 'Cette semaine', completed: 'Faits', plans: 'Plans actifs' },
      empty: {
        none: 'Aucun plan pour le moment',
        noneHint: 'Créez le premier : une machine, une camionnette, un ascenseur — et à quelle fréquence.',
        filtered: 'Aucun résultat',
        filteredHint: 'Essayez de retirer un filtre ou de chercher autre chose.',
      },
      pager: { previous: 'Précédent', next: 'Suivant', of: 'Page {page} sur {pages}' },

      form: {
        newTitle: 'Nouveau plan', editTitle: 'Modifier le plan',
        title: 'De quoi il s’agit', titlePlaceholder: 'Révision semestrielle, vidange…',
        kind: 'Type de maintenance',
        subject: 'Sur quoi', subjectHelp: 'Une camionnette, un emplacement, un client — ou saisissez-le s’il n’est enregistré nulle part.',
        subjectType: 'Type d’élément',
        subjectTypes: { vehicle: 'Véhicule', location: 'Emplacement d’entrepôt', customer: 'Client', project: 'Projet' },
        schedule: 'À quelle fréquence',
        mode: 'Compté en', every: 'Tous les', unit: 'Unité',
        anchor: 'Et compté depuis',
        everyUnits: 'Tous les', meterUnit: 'Unité du compteur',
        meterHelp: 'km pour les camionnettes, h pour les machines.',
        startsOn: 'Commence le', startsAtMeter: 'Compteur au départ',
        until: 'Se termine le', untilHelp: 'Laissez vide si cela ne se termine pas, ce qui est le cas normal.',
        maxOccurrences: 'Nombre de fois',
        instructions: 'Ce qu’il faut faire',
        checklist: 'Vérifications', checklistAdd: 'Ajouter une vérification',
        checklistHelp: 'Elles sont recopiées telles quelles sur chaque fiche, donc les modifier ne réécrit pas ce qui a déjà été validé.',
        assignee: 'Responsable', team: 'Équipe', notes: 'Notes internes',
        nextWillBe: 'Avec cela, la prochaine serait le {date}',
      },
      modes: { calendar: 'Calendrier', usage: 'Usage (compteur)', both: 'Le premier des deux' },
      units: { day: 'Jours', week: 'Semaines', month: 'Mois', year: 'Ans' },
      anchors: { completion: 'La dernière fois que cela a été fait', schedule: 'La date prévue' },
      anchorHelp: {
        completion: 'Le compte repart quand le travail est fait. Une vidange : faite en retard, la suivante suit.',
        schedule: 'La date reste la date, à l’heure ou non. Un contrôle réglementaire : être en retard n’achète rien de l’autre côté.',
      },

      plan: {
        title: 'Plan de maintenance', schedule: 'À quelle fréquence',
        lastDone: 'Dernière fois', nextDue: 'Prochaine fois', never: 'Jamais fait',
        meter: 'Compteur', doneCount: 'Nombre de fois',
        history: 'Historique', historyNone: 'Aucune fiche pour le moment',
        pausedNotice: 'Ce plan est suspendu : il n’apparaît plus dans ce qui est à faire tant qu’il n’est pas repris.',
        archivedNotice: 'Ce plan est archivé. Son historique est conservé en entier.',
        finishedNotice: 'Ce plan est arrivé au bout : il a atteint sa date de fin ou son nombre de fois.',
      },
      record: {
        completedAt: 'Date', completedBy: 'Qui', meter: 'Compteur',
        notes: 'Notes', void: 'Annuler cette fiche', voided: 'Annulée',
      },

      complete: {
        title: 'Marquer comme fait',
        completedAt: 'Quand cela a été fait', meter: 'Relevé du compteur',
        meterRequired: 'Ce plan fonctionne au compteur : le relevé est ce dont la prochaine échéance découle.',
        notes: 'Notes', checklist: 'Vérifications',
        confirm: 'Enregistrer la fiche', done: 'Fiche enregistrée',
        nextWillBe: 'La prochaine devient le {date}',
      },
      voidDialog: {
        title: 'Annuler la fiche',
        question: 'La fiche reste, marquée annulée, et le plan revient à ce que dit l’historique restant.',
        reason: 'Motif', reasonHint: 'Quelqu’un qui n’était pas là le lira dans trois ans.',
      },

      errors: {
        ARCHIVED: 'Ce plan est archivé',
        BAD_DATE: 'Ce n’est pas une date',
        IN_THE_FUTURE: 'Une fiche ne peut pas être datée dans le futur',
        BAD_METER: 'Un relevé doit être un nombre, et non négatif',
        METER_WENT_BACK: 'Ce relevé est inférieur au dernier enregistré sur ce plan',
        METER_IMPLAUSIBLE: 'Ce relevé est trop loin du dernier pour être un relevé ; quelqu’un qui gère la maintenance peut l’enregistrer',
        JUST_COMPLETED: 'Ce plan vient d’être clôturé à l’instant',
        METER_REQUIRED: 'Ce plan fonctionne au compteur : sans le relevé, la prochaine échéance ne peut pas être calculée',
      },
      search: { subtitle: 'Plan de maintenance' },
    },

    modules: { maintenance: 'Maintenance' },
    assistant: {
      openers: {
        maintenance: {
          'what-is-due': 'Quelle maintenance est à faire cette semaine ?',
          'what-is-overdue': 'Quelle maintenance est en retard ?',
          'last-serviced': 'Quand cela a-t-il été révisé pour la dernière fois ?',
        },
      },
    },
    permissions: {
      maintenance: {
        read: 'Voir les plans et ce qui est à faire',
        complete: 'Marquer une maintenance comme faite',
        write: 'Créer et modifier des plans de maintenance',
        manage: 'Suspendre, archiver et annuler des fiches',
      },
    },
    settings: {
      ns: {
        maintenance: {
          title: 'Maintenance',
          subtitle: 'Comment cette entreprise nomme ses maintenances, et le préavis qu’elle veut',
          planKinds: {
            label: 'Types de maintenance effectués par cette entreprise',
            help: 'Ce sont ceux que propose le sélecteur de chaque plan.',
          },
          noticeDays: { label: 'Jours de préavis avant qu’un plan commence à apparaître comme à venir' },
          defaultMeterUnit: {
            label: 'Unité du compteur par défaut',
            help: 'km pour les camionnettes, h pour les machines. Modifiable sur chaque plan.',
          },
          announceDue: {
            label: 'Annoncer sur le bus quand un plan arrive à échéance',
            help: 'Permet à d’autres modules de réagir — un ordre de travail qui s’ouvre seul, par exemple. Rien n’est créé ici.',
          },
        },
      },
    },
    audit: {
      actions: {
        maintenance: {
          plan_created: 'Plan de maintenance créé',
          template_created: 'Modèle de maintenance créé',
          template_updated: 'Modèle modifié',
          template_archived: 'Modèle archivé',
          template_restored: 'Modèle remis au catalogue',
          template_applied: 'Modèle appliqué à plusieurs machines',
          template_pushed: 'Modifications du modèle répercutées sur ses plans',
          shutdown_planned: 'Arrêt d’usine planifié',
          shutdown_updated: 'Arrêt d’usine modifié',
          shutdown_status_changed: 'État de l’arrêt modifié',
          plan_updated: 'Plan de maintenance modifié',
          plan_paused: 'Plan suspendu',
          plan_resumed: 'Plan repris',
          plan_archived: 'Plan archivé',
          completed: 'Maintenance marquée comme faite',
          record_voided: 'Fiche de maintenance annulée',
        },
      },
    },
  },
}

// El vocabulario de analítica del módulo, aparte porque es un contrato con el
// catálogo de `backend/analytics.js` y no copia de una pantalla. Ver ese fichero.
import { withAnalytics } from './i18n/analytics.js'

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'
// The reasons and field codes its errors carry (frontend/i18n/errorReasons.js).
import { withErrorReasons } from './i18n/errorReasons.js'

export const messages = withErrorReasons(withToolLabels(withAnalytics(baseMessages, 'maintenance')))
